import {
  formatDateToDDMMYYYY,
  formatDateToddmmyyhhmm,
} from '../../../utils/dateformatter';
import { PricingPeriodData } from '../../../constants/masterData';
import { api } from '../../../config';
import {
  LPG_PRODUCT_ID,
  GASOLINE_PRODUCT_ID,
  LPG_CARGO_RATIO_PRODUCT_TYPE_IDS,
  isGasolineProductId,
  usesDyedUndyedAlpha,
  getAlphaColumnLabel,
  getProcurementProductNameId,
  getProcurementProductTypeId,
  isLpgProduct,
  isLpgProcurement,
  requiresLpgCargoRatioSelection,
} from '../../../shared/offer-domain/constants';
import { getVisibleProcurementClauses } from '../../../shared/offer-domain/respond';
import { formatDate, formatMonth } from '../../../utils/format';

export {
  LPG_PRODUCT_ID,
  GASOLINE_PRODUCT_ID,
  LPG_CARGO_RATIO_PRODUCT_TYPE_IDS,
  isGasolineProductId,
  usesDyedUndyedAlpha,
  getAlphaColumnLabel,
  getProcurementProductNameId,
  getProcurementProductTypeId,
  isLpgProduct,
  isLpgProcurement,
  requiresLpgCargoRatioSelection,
  getVisibleProcurementClauses,
};

export const PROCUREMENT_TYPE_MAP = {
  '': '-',
  PelelanganKhusus: 'Pelelangan Khusus',
  PemilihanLangsung: 'Pemilihan Langsung',
  TunjukLangsung: 'Tunjuk Langsung',
};

const pick = (obj, camelKey, pascalKey) =>
  obj?.[camelKey] ?? obj?.[pascalKey] ?? null;

const normalizeProcurementClauseDetail = raw => {
  if (!raw || typeof raw !== 'object') return null;

  return {
    id: pick(raw, 'id', 'Id'),
    procurementId: pick(raw, 'procurementId', 'ProcurementId'),
    clauseDetailId: pick(raw, 'clauseDetailId', 'ClauseDetailId'),
    clauseTitle: pick(raw, 'clauseTitle', 'ClauseTitle'),
    ordreNumber: pick(raw, 'ordreNumber', 'OrdreNumber') ?? 0,
    isShow: pick(raw, 'isShow', 'IsShow') ?? false,
    isActive: pick(raw, 'isActive', 'IsActive'),
    clauseDescription: pick(raw, 'clauseDescription', 'ClauseDescription'),
    isShowToDMUT: pick(raw, 'isShowToDMUT', 'IsShowToDMUT') ?? false,
  };
};

const normalizeProcurementClauseDetails = raw => {
  const items =
    raw?.procurementClauseDetails ?? raw?.ProcurementClauseDetails ?? [];

  if (!Array.isArray(items)) return [];

  return items.map(normalizeProcurementClauseDetail).filter(Boolean);
};

const normalizeProcurementProductTypes = raw => {
  const items =
    raw?.procurementProductTypes ?? raw?.ProcurementProductTypes ?? [];
  if (!Array.isArray(items)) return [];

  return items
    .map(item => {
      if (!item || typeof item !== 'object') return null;
      const nested = item.productType ?? item.ProductType;
      const productTypeId =
        pick(item, 'productTypeId', 'ProductTypeId') ??
        pick(nested, 'id', 'Id');
      const productTypeName =
        pick(item, 'productTypeName', 'ProductTypeName') ??
        pick(nested, 'name', 'Name') ??
        pick(nested, 'description', 'Description');
      if (!productTypeId && !productTypeName) return null;
      return { productTypeId, productTypeName };
    })
    .filter(Boolean);
};

const resolveListProductTypeNames = item => {
  const direct = (
    item?.productTypeNames ??
    item?.ProductTypeNames ??
    []
  ).filter(Boolean);
  if (direct.length) return direct;
  return normalizeProcurementProductTypes(item)
    .map(entry => entry.productTypeName)
    .filter(Boolean);
};

export const formatProcurementProductTypeNames = procurement => {
  const names = (procurement?.procurementProductTypes ?? [])
    .map(entry => entry?.productTypeName)
    .filter(Boolean);
  if (!names.length) return procurement?.productType?.name ?? null;
  return names.join(', ');
};

const normalizeProductType = productTypeRaw => {
  if (!productTypeRaw) return null;
  const productRaw = productTypeRaw.product ?? productTypeRaw.Product;

  return {
    id: pick(productTypeRaw, 'id', 'Id'),
    name: pick(productTypeRaw, 'name', 'Name'),
    description: pick(productTypeRaw, 'description', 'Description'),
    product: productRaw
      ? {
          id: pick(productRaw, 'id', 'Id'),
          name: pick(productRaw, 'name', 'Name'),
        }
      : null,
  };
};

const normalizeNamedEntity = raw => {
  if (raw === null || raw === undefined || raw === '') return null;
  if (typeof raw !== 'object') {
    const text = String(raw).trim();
    if (!text) return null;
    return {
      id: null,
      name: text,
      description: null,
      currencyName: null,
      currencyCode: null,
    };
  }
  return {
    id: pick(raw, 'id', 'Id'),
    name:
      pick(raw, 'name', 'Name') ??
      pick(raw, 'description', 'Description') ??
      null,
    description: pick(raw, 'description', 'Description'),
    currencyName: pick(raw, 'currencyName', 'CurrencyName'),
    currencyCode: pick(raw, 'currencyCode', 'CurrencyCode'),
  };
};

const normalizePortRestrictions = raw => {
  const list = raw?.portRestriction ?? raw?.PortRestriction ?? [];
  if (!Array.isArray(list)) return [];

  return list.map(item => ({
    id: pick(item, 'id', 'Id'),
    locationId:
      pick(item, 'locationId', 'LocationId') ??
      pick(item, 'incotermLocationId', 'IncotermLocationId'),
    locationName:
      pick(item, 'locationName', 'LocationName') ??
      pick(item, 'incotermLocationName', 'IncotermLocationName') ??
      pick(item, 'name', 'Name'),
    incotermLocationId:
      pick(item, 'incotermLocationId', 'IncotermLocationId') ??
      pick(item, 'locationId', 'LocationId'),
    incotermLocationName:
      pick(item, 'incotermLocationName', 'IncotermLocationName') ??
      pick(item, 'locationName', 'LocationName'),
    name: pick(item, 'name', 'Name'),
    portName:
      pick(item, 'portName', 'PortName') ??
      pick(item, 'name', 'Name') ??
      pick(item, 'locationName', 'LocationName') ??
      pick(item, 'incotermLocationName', 'IncotermLocationName'),
    draftMin: pick(item, 'draftMin', 'DraftMin'),
    draftMax: pick(item, 'draftMax', 'DraftMax'),
    loaMin: pick(item, 'loaMin', 'LoaMin'),
    loaMax: pick(item, 'loaMax', 'LoaMax'),
    dwtMin: pick(item, 'dwtMin', 'DwtMin'),
    dwtMax: pick(item, 'dwtMax', 'DwtMax'),
    detailUrl:
      pick(item, 'detailUrl', 'DetailUrl') ??
      pick(item, 'link', 'Link') ??
      pick(item, 'url', 'Url'),
  }));
};

export const getPaymentCurrencyLabel = paymentCurrency => {
  if (!paymentCurrency) return '-';
  const name = paymentCurrency.currencyName ?? paymentCurrency.name;
  const code = paymentCurrency.currencyCode;
  if (name && code) return `${name} (${code})`;
  return name || code || '-';
};

export const normalizeProcurementDetail = raw => {
  if (!raw || typeof raw !== 'object') return null;

  const productTypeRaw = raw.productType ?? raw.ProductType;

  return {
    id: pick(raw, 'id', 'Id'),
    procurementNumber: pick(raw, 'procurementNumber', 'ProcurementNumber'),
    titleProcurement: pick(raw, 'titleProcurement', 'TitleProcurement'),
    description: pick(raw, 'description', 'Description'),
    procurementType: pick(raw, 'procurementType', 'ProcurementType'),
    procurementTypeHeaderLabel: pick(
      raw,
      'procurementTypeHeaderLabel',
      'ProcurementTypeHeaderLabel'
    ),
    contractType: pick(raw, 'contractType', 'ContractType'),
    contractPeriodSpot: pick(raw, 'contractPeriodSpot', 'ContractPeriodSpot'),
    contractPeriodTermStart: pick(
      raw,
      'contractPeriodTermStart',
      'ContractPeriodTermStart'
    ),
    contractPeriodTermEnd: pick(
      raw,
      'contractPeriodTermEnd',
      'ContractPeriodTermEnd'
    ),
    productType: normalizeProductType(productTypeRaw),
    procurementProductTypes: normalizeProcurementProductTypes(raw),
    operationalTolerance: pick(
      raw,
      'operationalTolerance',
      'OperationalTolerance'
    ),
    partyType: pick(raw, 'partyType', 'PartyType'),
    productSpecification: pick(
      raw,
      'productSpecification',
      'ProductSpecification'
    ),
    notesForQuantityAndDeliveryTerms: pick(
      raw,
      'notesForQuantityAndDeliveryTerms',
      'NotesForQuantityAndDeliveryTerms'
    ),
    quantityDiscrepancy: pick(
      raw,
      'quantityDiscrepancy',
      'QuantityDiscrepancy'
    ),
    delayPenaltyTerms: pick(raw, 'delayPenaltyTerms', 'DelayPenaltyTerms'),
    delivery: pick(raw, 'delivery', 'Delivery'),
    nominationProcedure: pick(
      raw,
      'nominationProcedure',
      'NominationProcedure'
    ),
    firstTimeSupply: pick(raw, 'firstTimeSupply', 'FirstTimeSupply'),
    redChannelPolicy: pick(raw, 'redChannelPolicy', 'RedChannelPolicy'),
    deliveryCustom: pick(raw, 'deliveryCustom', 'DeliveryCustom'),
    otherTermsAndCondition: pick(
      raw,
      'otherTermsAndCondition',
      'OtherTermsAndCondition'
    ),
    additionalNotes: pick(raw, 'additionalNotes', 'AdditionalNotes'),
    globalNotes: pick(raw, 'globalNotes', 'GlobalNotes'),
    isQuantityDiscrepancyMandatory: pick(
      raw,
      'isQuantityDiscrepancyMandatory',
      'IsQuantityDiscrepancyMandatory'
    ),
    isDelayPenaltyTermsMandatory: pick(
      raw,
      'isDelayPenaltyTermsMandatory',
      'IsDelayPenaltyTermsMandatory'
    ),
    isDeliveryMandatory: pick(
      raw,
      'isDeliveryMandatory',
      'IsDeliveryMandatory'
    ),
    isNominationProcedureMandatory: pick(
      raw,
      'isNominationProcedureMandatory',
      'IsNominationProcedureMandatory'
    ),
    isFirstTimeSupplyMandatory: pick(
      raw,
      'isFirstTimeSupplyMandatory',
      'IsFirstTimeSupplyMandatory'
    ),
    isRedChannelPolicyMandatory: pick(
      raw,
      'isRedChannelPolicyMandatory',
      'IsRedChannelPolicyMandatory'
    ),
    isDeliveryCustomMandatory: pick(
      raw,
      'isDeliveryCustomMandatory',
      'IsDeliveryCustomMandatory'
    ),
    invoicingQuantityId: pick(
      raw,
      'invoicingQuantityId',
      'InvoicingQuantityId'
    ),
    invoicingQuantity: normalizeNamedEntity(
      raw.invoicingQuantity ?? raw.InvoicingQuantity
    ),
    modeInvoicingQuantityId: pick(
      raw,
      'modeInvoicingQuantityId',
      'ModeInvoicingQuantityId'
    ),
    modeInvoicingQuantity: normalizeNamedEntity(
      raw.modeInvoicingQuantity ?? raw.ModeInvoicingQuantity
    ),
    paymentCurrency: normalizeNamedEntity(
      raw.paymentCurrency ?? raw.PaymentCurrency
    ),
    delayPenaltyAmount: pick(raw, 'delayPenaltyAmount', 'DelayPenaltyAmount'),
    descriptionOfDelayPenaltyTerms: pick(
      raw,
      'descriptionOfDelayPenaltyTerms',
      'DescriptionOfDelayPenaltyTerms'
    ),
    descriptionOfPaymentTerms: pick(
      raw,
      'descriptionOfPaymentTerms',
      'DescriptionOfPaymentTerms'
    ),
    law: normalizeNamedEntity(raw.law ?? raw.Law),
    arbitration: normalizeNamedEntity(raw.arbitration ?? raw.Arbitration),
    portRestriction: normalizePortRestrictions(raw),
    proposalSubmissionDeadline: pick(
      raw,
      'proposalSubmissionDeadline',
      'ProposalSubmissionDeadline'
    ),
    proposalValidityPeriodMax: pick(
      raw,
      'proposalValidityPeriodMax',
      'ProposalValidityPeriodMax'
    ),
    submittedDateTenderOpening: pick(
      raw,
      'submittedDateTenderOpening',
      'SubmittedDateTenderOpening'
    ),
    tenderOpeningStatus: pick(
      raw,
      'tenderOpeningStatus',
      'TenderOpeningStatus'
    ),
    isRequestHpsCompleted:
      pick(raw, 'isRequestHpsCompleted', 'IsRequestHpsCompleted') ??
      pick(raw, 'isHPSRequestCompleted', 'IsHPSRequestCompleted'),
    isInviteDMUTCompleted:
      pick(raw, 'isInviteDMUTCompleted', 'IsInviteDMUTCompleted') ??
      pick(raw, 'isDMUTInvitationCompleted', 'IsDMUTInvitationCompleted'),
    isInviteDMUTCompletedDate: pick(
      raw,
      'isInviteDMUTCompletedDate',
      'IsInviteDMUTCompletedDate'
    ),
    isInvitePanitiaTenderCompleted: pick(
      raw,
      'isInvitePanitiaTenderCompleted',
      'IsInvitePanitiaTenderCompleted'
    ),
    isInvitePanitiaTenderCompletedDate: pick(
      raw,
      'isInvitePanitiaTenderCompletedDate',
      'IsInvitePanitiaTenderCompletedDate'
    ),
    isReqFreightAvailabilityCompleted: pick(
      raw,
      'isReqFreightAvailabilityCompleted',
      'IsReqFreightAvailabilityCompleted'
    ),
    isReqFreightAvailabilityEnable: pick(
      raw,
      'isReqFreightAvailabilityEnable',
      'IsReqFreightAvailabilityEnable'
    ),
    hpsStatus: pick(raw, 'hpsStatus', 'HPSStatus'),
    noNotaDinas: pick(raw, 'noNotaDinas', 'NoNotaDinas'),
    hpsRequestDate: pick(raw, 'hpsRequestDate', 'HPSRequestDate'),
    dateNotaDinas: pick(raw, 'dateNotaDinas', 'DateNotaDinas'),
    titleNotaDinas: pick(raw, 'titleNotaDinas', 'TitleNotaDinas'),
    userIdamanId: pick(raw, 'userIdamanId', 'UserIdamanId'),
    hpsRequestNumber: pick(raw, 'hpsRequestNumber', 'HPSRequestNumber'),
    freightRequestNumber: pick(
      raw,
      'freightRequestNumber',
      'FreightRequestNumber'
    ),
    freightRequestDate: pick(raw, 'freightRequestDate', 'FreightRequestDate'),
    documentNotaDinas: pick(raw, 'documentNotaDinas', 'DocumentNotaDinas'),
    documentNotaDinasName: pick(
      raw,
      'documentNotaDinasName',
      'DocumentNotaDinasName'
    ),
    documentNotaDinasUrl: pick(
      raw,
      'documentNotaDinasUrl',
      'DocumentNotaDinasUrl'
    ),
    documentNotaDinasRilis: pick(
      raw,
      'documentNotaDinasRilis',
      'DocumentNotaDinasRilis'
    ),
    documentNotaDinasRilisUrl: pick(
      raw,
      'documentNotaDinasRilisUrl',
      'DocumentNotaDinasRilisUrl'
    ),
    invitationEventDate: pick(
      raw,
      'invitationEventDate',
      'InvitationEventDate'
    ),
    invitationStartTime: pick(
      raw,
      'invitationStartTime',
      'InvitationStartTime'
    ),
    invitationEndTime: pick(raw, 'invitationEndTime', 'InvitationEndTime'),
    invitationTillEnd: pick(raw, 'invitationTillEnd', 'InvitationTillEnd'),
    invitationLocation: pick(raw, 'invitationLocation', 'InvitationLocation'),
    invitationAgenda: pick(raw, 'invitationAgenda', 'InvitationAgenda'),
    dataProcurmentLain: pick(raw, 'dataProcurmentLain', 'DataProcurmentLain'),
    procurementClauseDetails: normalizeProcurementClauseDetails(raw),
    gtcs: pick(raw, 'gtcs', 'Gtcs') ?? [],
  };
};

export const unwrapProcurement = response => {
  if (!response) return null;

  const isApiWrapper =
    response.data &&
    response.procurementNumber === undefined &&
    response.ProcurementNumber === undefined &&
    response.titleProcurement === undefined &&
    response.TitleProcurement === undefined;

  const raw = isApiWrapper ? response.data : response;
  return normalizeProcurementDetail(raw);
};

export const getProcurementTypeLabel = type =>
  PROCUREMENT_TYPE_MAP[type] || type || '-';

export const getContractTypeLabel = contractType => {
  const value = parseInt(contractType, 10);
  if (value === 1) return 'TERM';
  if (value === 2) return 'SPOT';
  return '-';
};

export const formatPeriodMonth = formatMonth;

export const formatPeriodRange = procurement => {
  if (!procurement) return '-';
  const contractType = parseInt(procurement.contractType, 10);
  if (contractType === 2) {
    return formatPeriodMonth(
      procurement.contractPeriodSpot || procurement.contractPeriodTermStart
    );
  }
  if (contractType === 1) {
    const start = formatPeriodMonth(procurement.contractPeriodTermStart);
    const end = formatPeriodMonth(procurement.contractPeriodTermEnd);
    if (start === '-' && end === '-') return '-';
    return `${start} - ${end}`;
  }
  return '-';
};

export const isSpotContract = procurement =>
  parseInt(procurement?.contractType, 10) === 2;

export const getContractPeriodProps = procurement => {
  if (!procurement) {
    return {
      isSpot: false,
      startLabel: 'Start Period',
      startValue: '-',
      endLabel: 'End Period',
      endValue: '-',
    };
  }

  if (isSpotContract(procurement)) {
    return {
      isSpot: true,
      startLabel: 'Period',
      startValue: formatPeriodMonth(
        procurement.contractPeriodSpot || procurement.contractPeriodTermStart
      ),
      endLabel: null,
      endValue: null,
    };
  }

  return {
    isSpot: false,
    startLabel: 'Start Period',
    startValue: formatPeriodMonth(procurement.contractPeriodTermStart),
    endLabel: 'End Period',
    endValue: formatPeriodMonth(procurement.contractPeriodTermEnd),
  };
};

export const formatSubmittedDate = formatDate;

export const getTenderOpeningStatusConfig = status => {
  const statusCode =
    status === '' || status === null || status === undefined
      ? 0
      : Number(status);

  switch (statusCode) {
    case 1:
      return {
        label: 'In Progress',
        tone: 'warning',
        className: 'bg-warning text-light',
      };
    case 2:
      return {
        label: 'Done',
        tone: 'success',
        className: 'bg-success text-light',
      };
    case 0:
    default:
      return {
        label: 'Open',
        tone: 'info',
        className: 'bg-primary text-light',
      };
  }
};

export const displayValue = value => {
  if (value === null || value === undefined || value === '') return '-';
  return value;
};

const getDownloadFilenameDatePart = submittedDate => {
  if (!submittedDate) return String(Date.now());
  const formatted = formatDateToDDMMYYYY(submittedDate);
  return formatted ? formatted.replace(/\//g, '_') : String(Date.now());
};

export const buildTenderOpeningDownloadFilename = ({
  procurementNumber,
  title,
  submittedDate,
  extension,
}) => {
  const kode = procurementNumber || 'UNKNOWN';
  const datePart = getDownloadFilenameDatePart(submittedDate);
  return `${kode}_${title}_${datePart}.${extension}`;
};

export const getTenderOpeningTabCompletion = (procurement, completionKey) => {
  if (!procurement || !completionKey) return null;
  return procurement[completionKey] ?? null;
};

export const formatPricingPeriodRange = (start, end) => {
  const startLabel = start ? formatDateToDDMMYYYY(start) : '';
  const endLabel = end ? formatDateToDDMMYYYY(end) : '';
  if (!startLabel && !endLabel) return '-';
  if (startLabel && endLabel) return `${startLabel} - ${endLabel}`;
  return startLabel || endLabel;
};

export const getPricingPeriodLabel = pricingPeriod => {
  const value = parseInt(pricingPeriod, 10);
  const option = PricingPeriodData[0]?.options?.find(
    item => item.value === value
  );
  if (option?.label) return option.label;
  if (
    pricingPeriod === null ||
    pricingPeriod === undefined ||
    pricingPeriod === ''
  ) {
    return '-';
  }
  return String(pricingPeriod);
};

export const getPricingPeriodDetailLabel = pricingPeriod => {
  const value = parseInt(pricingPeriod, 10);
  if (value === 1) return 'Publish Index Period';
  if (value === 2) return 'Schema B/L Date';
  if (value === 3) return 'Fixed Price';
  return 'Pricing Detail';
};

export const getPricingPeriodDetailValue = data => {
  if (!data) return '-';
  const value = parseInt(data.pricingPeriod, 10);
  if (value === 1) {
    return formatPricingPeriodRange(
      data.startDatePublishIndex,
      data.endDatePublishIndex
    );
  }
  if (value === 2) return data.schemaBL || '-';
  if (value === 3) {
    if (data.priceAmount === null || data.priceAmount === undefined) return '-';
    return data.priceAmount;
  }
  return '-';
};

export const formatProposalDeadline = isoDate =>
  isoDate ? formatDateToddmmyyhhmm(isoDate) : '-';

export const getTenderOpeningBadgeColor = status => {
  const lower = (status || 'open').toString().toLowerCase();
  if (lower === 'done') return 'success';
  if (lower === 'inprogress' || lower === 'in progress') return 'warning';
  return 'primary';
};

const getTenderOpeningStatusLabel = status => {
  if (status === null || status === undefined || status === '') return 'Open';

  const numericStatus = parseInt(status, 10);
  if (numericStatus === 0) return 'Open';
  if (numericStatus === 1) return 'In Progress';
  if (numericStatus === 2) return 'Done';

  return String(status);
};

export const getLatestTenderOpeningUpdateDate = item => {
  if (!item) return null;

  const timestamps = [
    pick(item, 'hpsRequestDate', 'HPSRequestDate'),
    pick(item, 'isInviteDMUTCompletedDate', 'IsInviteDMUTCompletedDate'),
    pick(
      item,
      'isInvitePanitiaTenderCompletedDate',
      'IsInvitePanitiaTenderCompletedDate'
    ),
    pick(item, 'freightRequestDate', 'FreightRequestDate'),
  ]
    .map(value => {
      if (!value) return null;
      const time = new Date(value).getTime();
      return Number.isNaN(time) ? null : time;
    })
    .filter(time => time !== null);

  if (!timestamps.length) return null;
  return new Date(Math.max(...timestamps)).toISOString();
};

export const mapProcurementToTenderOpeningRow = item => {
  if (!item) return null;

  const productTypeRaw = item.productType ?? item.ProductType;
  const productRaw = productTypeRaw?.product ?? productTypeRaw?.Product;
  const submittedDateTenderOpening = pick(
    item,
    'submittedDateTenderOpening',
    'SubmittedDateTenderOpening'
  );
  const tenderOpeningStatus = pick(
    item,
    'tenderOpeningStatus',
    'TenderOpeningStatus'
  );
  const latestUpdateDate = getLatestTenderOpeningUpdateDate(item);

  return {
    id: pick(item, 'id', 'Id'),
    idProc: pick(item, 'procurementNumber', 'ProcurementNumber'),
    submittedDate: submittedDateTenderOpening
      ? formatDateToDDMMYYYY(submittedDateTenderOpening)
      : '-',
    updateDate: latestUpdateDate ? formatDateToDDMMYYYY(latestUpdateDate) : '-',
    updateDateRaw: latestUpdateDate ? new Date(latestUpdateDate).getTime() : 0,
    title: pick(item, 'titleProcurement', 'TitleProcurement'),
    period: getContractTypeLabel(pick(item, 'contractType', 'ContractType')),
    procType: getProcurementTypeLabel(
      pick(item, 'procurementType', 'ProcurementType')
    ),
    productType: {
      main: productRaw?.name ?? productRaw?.Name ?? '-',
      sub: (() => {
        const names = resolveListProductTypeNames(item);
        if (names.length > 1) return names.join(', ');
        return (
          productTypeRaw?.description ?? productTypeRaw?.Description ?? '-'
        );
      })(),
    },
    reqHps:
      pick(item, 'isRequestHpsCompleted', 'IsRequestHpsCompleted') ??
      item.isHPSRequestCompleted ??
      item.IsHPSRequestCompleted,
    inviteDmut:
      pick(item, 'isInviteDMUTCompleted', 'IsInviteDMUTCompleted') ??
      item.isDMUTInvitationCompleted ??
      item.IsDMUTInvitationCompleted,
    invitePanitia: pick(
      item,
      'isInvitePanitiaTenderCompleted',
      'IsInvitePanitiaTenderCompleted'
    ),
    reqFreight: pick(
      item,
      'isReqFreightAvailabilityCompleted',
      'IsReqFreightAvailabilityCompleted'
    ),
    isReqFreightAvailabilityEnable: pick(
      item,
      'isReqFreightAvailabilityEnable',
      'IsReqFreightAvailabilityEnable'
    ),
    status: getTenderOpeningStatusLabel(tenderOpeningStatus),
    statusCode: tenderOpeningStatus,
    contractType: pick(item, 'contractType', 'ContractType'),
    proposalSubmissionDeadline: pick(
      item,
      'proposalSubmissionDeadline',
      'ProposalSubmissionDeadline'
    ),
  };
};

export const unwrapTenderOpeningList = result => {
  const payload = result?.data ?? result ?? {};
  const items = payload.items ?? payload.Items ?? [];
  const totalItems = payload.totalItems ?? payload.TotalItems ?? 0;

  return {
    items: items.map(mapProcurementToTenderOpeningRow).filter(Boolean),
    totalItems,
  };
};

const isTruthyCompletion = value =>
  value === true || value === 1 || value === '1' || value === 'true';

export const getTenderOpeningStepFlags = procurement => {
  if (!procurement) return null;

  return {
    isRequestHpsCompleted:
      isTruthyCompletion(
        pick(procurement, 'isRequestHpsCompleted', 'IsRequestHpsCompleted')
      ) ||
      isTruthyCompletion(
        pick(procurement, 'isHPSRequestCompleted', 'IsHPSRequestCompleted')
      ),
    isInviteDMUTCompleted:
      isTruthyCompletion(
        pick(procurement, 'isInviteDMUTCompleted', 'IsInviteDMUTCompleted')
      ) ||
      isTruthyCompletion(
        pick(
          procurement,
          'isDMUTInvitationCompleted',
          'IsDMUTInvitationCompleted'
        )
      ),
    isInvitePanitiaTenderCompleted: isTruthyCompletion(
      pick(
        procurement,
        'isInvitePanitiaTenderCompleted',
        'IsInvitePanitiaTenderCompleted'
      )
    ),
    isReqFreightAvailabilityCompleted: isTruthyCompletion(
      pick(
        procurement,
        'isReqFreightAvailabilityCompleted',
        'IsReqFreightAvailabilityCompleted'
      )
    ),
    isReqFreightAvailabilityEnable: isTruthyCompletion(
      pick(
        procurement,
        'isReqFreightAvailabilityEnable',
        'IsReqFreightAvailabilityEnable'
      )
    ),
  };
};

export const calculateTenderOpeningStatus = procurement => {
  const flags = getTenderOpeningStepFlags(procurement);
  if (!flags) {
    return { tenderOpeningStatus: 0, submittedDateTenderOpening: null };
  }

  const completedSteps = [
    flags.isRequestHpsCompleted,
    flags.isInviteDMUTCompleted,
  ];

  if (flags.isReqFreightAvailabilityEnable) {
    completedSteps.push(flags.isReqFreightAvailabilityCompleted);
  }

  const anyCompleted = completedSteps.some(Boolean);
  const allCompleted = completedSteps.every(Boolean);

  if (allCompleted) {
    return {
      tenderOpeningStatus: 2,
      submittedDateTenderOpening: new Date().toISOString(),
    };
  }

  if (anyCompleted) {
    return {
      tenderOpeningStatus: 1,
      submittedDateTenderOpening: null,
    };
  }

  return {
    tenderOpeningStatus: 0,
    submittedDateTenderOpening: null,
  };
};
const stripFileExtension = fileName => {
  if (fileName == null) return null;
  const normalized = String(fileName).trim();
  if (!normalized) return null;
  return normalized.replace(/\.[^/.]+$/, '');
};

export const unwrapProcurementDocumentItems = payload => {
  if (!payload) return [];

  const root = payload?.data ?? payload?.Data ?? payload;
  const nested = root?.data ?? root?.Data ?? root;

  const candidates = [
    nested?.items,
    nested?.Items,
    root?.items,
    root?.Items,
    Array.isArray(nested) ? nested : null,
    Array.isArray(root) ? root : null,
  ];

  const items = candidates.find(Array.isArray);
  return items ?? [];
};

export const mapProcurementDocumentItem = (item, formatDate) => {
  if (!item || typeof item !== 'object') return null;

  const link = pick(item, 'link', 'Link');
  const rawCustomFileName = pick(item, 'customFileName', 'CustomFileName');
  const customFileName = stripFileExtension(rawCustomFileName);
  const documentUpload = item?.documentUpload ?? item?.DocumentUpload;

  const defaultFormatDate = dateStr => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const format = formatDate ?? defaultFormatDate;

  return {
    id: pick(item, 'id', 'Id') ?? null,
    customFileName,
    documentName:
      documentUpload?.documentName ?? documentUpload?.DocumentName ?? null,
    documentUploadId:
      pick(item, 'documentUploadId', 'DocumentUploadId') ?? null,
    actualUrl: link ?? null,
    isMasterDocument: !link,
    isShowToDMUT: pick(item, 'isShowToDMUT', 'IsShowToDMUT') ?? false,
    lastUpdated: format(pick(item, 'updatedDate', 'UpdatedDate')),
    createdDate: format(pick(item, 'createdDate', 'CreatedDate')),
  };
};

export const mapProcurementDocuments = (payload, formatDate) =>
  unwrapProcurementDocumentItems(payload)
    .map(item => mapProcurementDocumentItem(item, formatDate))
    .filter(Boolean);

export const fetchProductTypePricingUnit = async (productTypeId, token) => {
  if (!productTypeId || !token) return null;
  const response = await fetch(`${api.API_URL}/product-type/${productTypeId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
    },
  });
  if (!response.ok) return null;
  const data = await response.json();
  return (
    data?.pricingUnit ??
    data?.PricingUnit ??
    data?.data?.pricingUnit ??
    data?.data?.PricingUnit ??
    null
  );
};
